import { c as _c } from "react/compiler-runtime";
import { IcoChevronRight16, IcoWarning16 } from '@onefootprint/icons';
import type { ChatSessionResponse } from '@onefootprint/request-types/dashboard';
import { cx } from 'class-variance-authority';
import { useState } from 'react';
import type { ChatMessage as ChatMessageData } from '../../hooks/use-chat-ws';
import { hasVisibleContent, summarizeToolCalls } from '../../utils/tool-call-summary';
import ChatMessage, { getToolDisplay } from '../chat-message';
type ToolCallGroupProps = {
  messages: ChatMessageData[];
  session: ChatSessionResponse;
  toolResults?: Record<string, string>;
  /**
   * Per-tool-use-id error flags from `useChatMessages`. A tool with an entry here
   * still has a `toolResults[id]` content string (the error payload) — but it
   * shouldn't be treated as in-progress, and rows render with a warning icon.
   * The summary line gets an inline warning when any tool in the group errored.
   *
   * Live `WsToolResultEvent` does not currently carry `is_error`, so in-session
   * errors won't populate this map — only history-loaded errors will. Bug for
   * the BE team noted in `use-chat-messages.ts`.
   */
  toolErrors?: Record<string, boolean>;
  isThinking?: boolean;
  summary?: string;
  showNarration?: boolean;
};
export const ToolCallGroup = t0 => {
  const $ = _c(19);
  const {
    messages,
    session,
    toolResults: t1,
    toolErrors: t2,
    isThinking,
    summary: summaryOverride,
    showNarration: t3
  } = t0;
  let t4;
  if ($[0] !== t1) {
    t4 = t1 === undefined ? {} : t1;
    $[0] = t1;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const toolResults = t4;
  let t5;
  if ($[2] !== t2) {
    t5 = t2 === undefined ? {} : t2;
    $[2] = t2;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const toolErrors = t5;
  const showNarration = t3 === undefined ? false : t3;
  let t6;
  if ($[4] !== isThinking || $[5] !== showNarration) {
    t6 = m => hasVisibleContent(m, isThinking) || showNarration && isNarration(m);
    $[4] = isThinking;
    $[5] = showNarration;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const visible = messages.filter(t6);
  const seen = new Set();
  const deduped = visible.filter(m_0 => {
    if (m_0.type !== "tool_use") {
      return true;
    }
    const info = getToolDisplay(m_0.tool, m_0.input);
    const hasDetail = info?.detail != null && info.detail !== "";
    const key = hasDetail ? `${m_0.tool}::${JSON.stringify(m_0.input)}` : `${m_0.tool}::${info?.label ?? ""}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
  const [expanded, setExpanded] = useState(false);
  if (visible.length === 0) {
    return null;
  }
  const summary = summaryOverride ?? summarizeToolCalls(visible);
  const toolCount = visible.filter(_temp).length;
  let t7;
  if ($[7] !== toolResults) {
    t7 = m_2 => m_2.type === "tool_use" && !toolResults[m_2.id];
    $[7] = toolResults;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const anyInProgress = visible.some(t7);
  let t8;
  if ($[9] !== toolErrors) {
    t8 = m_3 => m_3.type === "tool_use" && toolErrors[m_3.id];
    $[9] = toolErrors;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const errorCount = visible.filter(t8).length;
  const hasErrors = errorCount > 0;
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => setExpanded(_temp2);
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== errorCount || $[13] !== hasErrors) {
    t10 = hasErrors && <span className="text-error">{" \xB7 "}{errorCount === 1 ? "1 error" : `${errorCount} errors`}</span>;
    $[12] = errorCount;
    $[13] = hasErrors;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const t11 = expanded && "rotate-90";
  let t12;
  if ($[15] !== t11) {
    t12 = cx("shrink-0 text-quaternary transition-transform", t11);
    $[15] = t11;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== t12) {
    t13 = <IcoChevronRight16 className={t12} />;
    $[17] = t12;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  return <div className="flex flex-col"><button type="button" onClick={t9} className="group/tc inline-flex items-center gap-1.5 self-start max-w-full text-left cursor-pointer">{hasErrors && !anyInProgress && <span role="img" aria-label="Some steps failed" className="shrink-0 inline-flex items-center justify-center"><IcoWarning16 color="error" /></span>}<span className={cx("text-caption-1 truncate transition-colors", hasErrors ? "text-error" : "text-tertiary group-hover/tc:text-secondary", anyInProgress && !expanded && "text-shimmer")}>{summary || `${toolCount} tool call${toolCount === 1 ? "" : "s"}`}{t10}</span>{t13}</button>{expanded && <ol className="mt-3 mb-1 flex flex-col gap-2">{deduped.map((msg, idx) => <li key={msg.type === "tool_use" ? msg.id : `step-${idx}`} className="min-w-0">{isNarration(msg) ? <p className="text-caption-1 text-tertiary whitespace-pre-wrap break-words line-clamp-3">{msg.content}</p> : <ChatMessage message={msg} session={session} toolResult={msg.type === "tool_use" ? toolResults[msg.id] : undefined} toolResultIsError={msg.type === "tool_use" ? !!toolErrors[msg.id] : undefined} hideIcon={true} />}</li>)}</ol>}</div>;
};
const isNarration = (msg: ChatMessageData): msg is Extract<ChatMessageData, {
  type: 'thinking' | 'text';
}> => (msg.type === 'thinking' || msg.type === 'text') && msg.content.trim().length > 0;
export default ToolCallGroup;
function _temp(m_1) {
  return m_1.type === "tool_use";
}
function _temp2(e) {
  return !e;
}